import React, {useState, useRef, useCallback, useMemo} from 'react';
import {Button, Popover, Tag} from 'antd';
import {
  MenuUnfoldOutlined,
  PlusCircleOutlined,
  FontColorsOutlined,
  HolderOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  EllipsisOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  RobotOutlined,
  FilterOutlined
} from '@ant-design/icons';
import {FieldInfo, ChartConfig, FilterRule} from '../../types';
import {AIGenerateModal} from './AIGenerateModal';
import {FilterPanel} from './FilterPanel';
import './style.css';

interface SheetHeaderToolbarProps {
  siderCollapsed: boolean;
  onToggleSider: () => void;
  onAddRow: () => void;
  fieldList: FieldInfo[];
  draggingField: string | null;
  handleFieldDragStart: (
    index: number,
    field: string
  ) => (e: React.DragEvent) => void;
  handleFieldDrop: (index: number) => (e: React.DragEvent) => void;
  handleToggleColumnVisibility: (field: string) => void;
  currentSheetPermissions: string[];
  currentSheetCreateUserId: string;
  userId: string;
  isLoggedIn: boolean;
  onAddChartsToDashboard: (charts: ChartConfig[]) => void;
  currentSheetId?: string;
  api: any;
  props: any;
  onApplyFilter?: (filters: FilterRule[], logicRelation?: 'and' | 'or') => void;
  activeFilters?: FilterRule[];
  currentFilterLogicRelation?: 'and' | 'or';
  activeViewId: string;
}

export const SheetHeaderToolbar: React.FC<SheetHeaderToolbarProps> = ({
  siderCollapsed,
  onToggleSider,
  onAddRow,
  fieldList,
  draggingField,
  handleFieldDragStart,
  handleFieldDrop,
  handleToggleColumnVisibility,
  currentSheetPermissions,
  currentSheetCreateUserId,
  userId,
  isLoggedIn,
  onAddChartsToDashboard,
  currentSheetId,
  api,
  props,
  onApplyFilter,
  activeFilters = [],
  currentFilterLogicRelation = 'and',
  activeViewId
}) => {
  const [isAIModalVisible, setIsAIModalVisible] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  // 权限判断逻辑 - 使用 useMemo 缓存
  const hasEditPermission = useMemo(() => {
    const isCreator = currentSheetCreateUserId === userId;
    return isCreator || currentSheetPermissions.includes('create');
  }, [currentSheetCreateUserId, userId, currentSheetPermissions]);
  console.log('activeViewId1111:', activeViewId);

  // 字段管理内容 - 使用 useMemo 缓存
  const fieldManagementContent = useMemo(
    () => (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '200px',
          maxHeight: '300px',
          overflowY: 'auto',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
      >
        {fieldList
          .filter(col => col.field !== 'addColumn')
          .map((item, idx) => {
            const isDragging = draggingField === item.field;
            return (
              <div
                key={item.field}
                draggable
                onDragStart={handleFieldDragStart(idx, item.field)}
                onDragOver={e => e.preventDefault()}
                onDrop={handleFieldDrop(idx)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 0',
                  cursor: 'grab',
                  borderRadius: '4px',
                  background: isDragging ? '#f0f0f0' : 'transparent',
                  boxShadow: isDragging ? '0 4px 8px rgba(0,0,0,0.2)' : 'none',
                  transform: isDragging ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.2s ease-in-out'
                }}
                onMouseDown={e => (e.currentTarget.style.cursor = 'grabbing')}
                onMouseUp={e => (e.currentTarget.style.cursor = 'grab')}
              >
                <HolderOutlined style={{marginRight: 10, marginBottom: 5}} />
                <span style={{flex: 1}}>{item.title}</span>
                <Button
                  type="text"
                  size="small"
                  icon={
                    item.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                  }
                  onClick={() => handleToggleColumnVisibility(item.field)}
                />
                <EllipsisOutlined style={{marginLeft: 8, cursor: 'pointer'}} />
              </div>
            );
          })}
      </div>
    ),
    [
      fieldList,
      draggingField,
      handleFieldDragStart,
      handleFieldDrop,
      handleToggleColumnVisibility
    ]
  );

  // 权限标签 - 使用 useMemo 缓存
  const permissionTag = useMemo(() => {
    if (!isLoggedIn) return null;

    const permsLength = currentSheetPermissions.length;
    const isCreator = currentSheetCreateUserId === userId;
    let text, color, icon;

    if (isCreator) {
      text = '全部权限';
      color = 'success';
      icon = <CheckCircleOutlined />;
    } else if (permsLength === 0) {
      text = '无权限';
      color = 'error';
      icon = <CloseCircleOutlined />;
    } else if (permsLength === 1 && currentSheetPermissions[0] === 'read') {
      text = '仅浏览';
      color = 'warning';
      icon = <ExclamationCircleOutlined />;
    } else if (permsLength === 4) {
      text = '全部权限';
      color = 'success';
      icon = <CheckCircleOutlined />;
    } else {
      text = '可编辑';
      color = 'processing';
      icon = <SyncOutlined spin />;
    }

    return (
      <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
        <Tag 
          icon={icon} 
          color={color}
          className="permission-tag"
          style={{
            borderRadius: '20px',
            padding: '4px 12px',
            fontSize: '13px',
            fontWeight: '500',
            border: 'none',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {text}
        </Tag>
        {isCreator && (
          <Tag 
            color="default"
            className="creator-tag"
            style={{
              borderRadius: '20px',
              padding: '4px 12px',
              fontSize: '12px',
              fontWeight: '400',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              color: 'rgba(0, 0, 0, 0.7)',
            }}
          >
            创建者: 我
          </Tag>
        )}
      </div>
    );
  }, [isLoggedIn, currentSheetPermissions, currentSheetCreateUserId, userId]);

  // 处理筛选应用
  const handleApplyFilters = useCallback(
    (filters: FilterRule[], logicRelation: 'and' | 'or' = 'and') => {
      setIsFilterVisible(false);
      onApplyFilter?.(filters, logicRelation);
    },
    [onApplyFilter]
  );

  // 处理清除筛选
  const handleClearFilters = useCallback(() => {
    setIsFilterVisible(false);
    onApplyFilter?.([], 'and');
  }, [onApplyFilter]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background:'rgba(255, 255, 255, 0.1)',
          padding: '10px 0',
          borderRadius: '30px',
          // border: '4px solid #fff',
        }}
      >
        <div
          className="header"
          style={{display: 'flex', gap: '10px', alignItems: 'center'}}
        >
          {siderCollapsed && (
            <MenuUnfoldOutlined
              className="header-icon"
              onClick={onToggleSider}
            />
          )}
          {activeViewId === 'default' && (
            <>
              <button
                className='glass-button'
                // type="text"
                onClick={onAddRow}
                // icon={<PlusCircleOutlined />}
                style={{color: hasEditPermission ? '' : '#B8B8B9'}}
                disabled={!hasEditPermission}
              >
                <PlusCircleOutlined /> 添加一行
              </button>

              {hasEditPermission ? (
                <Popover
                  content={fieldManagementContent}
                  title="字段管理"
                  trigger="click"
                  placement="bottomLeft"
                  overlayClassName="no-arrow-popover"
                >
                  <button className='glass-button'>
                    <FontColorsOutlined /> 字段管理
                  </button>
                </Popover>
              ) : (
                <button className='glass-button' disabled>
                  <FontColorsOutlined /> 字段管理
                </button>
              )}
            </>
          )}

          {/* <Button
            ref={filterButtonRef}
            type="text"
            icon={<FilterOutlined />}
            onClick={() => setIsFilterVisible(!isFilterVisible)}
            style={{
              color: activeFilters.length > 0 ? '#1677FF' : undefined,
              fontWeight: activeFilters.length > 0 ? 'bold' : 'normal'
            }}
          >
            筛选{' '}
            {activeFilters.length > 0 && (
              <span style={{marginLeft: 4, fontSize: 12}}>
                ({activeFilters.length})
              </span>
            )}
          </Button> */}
          <button
            className='glass-button'
            ref={filterButtonRef}
            onClick={() => setIsFilterVisible(!isFilterVisible)}
            style={{
              color: activeFilters.length > 0 ? '' : undefined,
              fontWeight: activeFilters.length > 0 ? 'bold' : 'normal'
            }}
          >
            <FilterOutlined /> 筛选{' '}
            {activeFilters.length > 0 && (
              <span style={{marginLeft: 4, fontSize: 12}}>
                ({activeFilters.length})
              </span>
            )}
          </button>

          {activeViewId === 'default' && (
            <button
              className='glass-button ai-btn'
              onClick={() => setIsAIModalVisible(true)}
              style={{color: hasEditPermission ? '' : '#B8B8B9'}}
              disabled={!hasEditPermission}
            >
              <RobotOutlined /> AI生成仪表盘
            </button>
          )}
        </div>
        {permissionTag}
      </div>

      {/* 筛选弹窗 */}
      <FilterPanel
        visible={isFilterVisible}
        onClose={() => setIsFilterVisible(false)}
        fieldList={fieldList}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        activeFilters={activeFilters}
        anchorElement={filterButtonRef.current}
        initialLogicRelation={currentFilterLogicRelation}
      />

      {/* AI生成模态框组件 */}
      <AIGenerateModal
        visible={isAIModalVisible}
        onClose={() => setIsAIModalVisible(false)}
        onConfirm={onAddChartsToDashboard}
        currentSheetId={currentSheetId}
        api={api}
        fieldList={fieldList}
        props={props}
      />
    </>
  );
};
